import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { BASE_URL } from "../../utils/api";
import ExportButtons from "./ExportButtons";
// Icon Imports
import {
  Mail,
  Phone,
  Globe,
  MapPin,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
} from "lucide-react";

const CompanyViewModal = ({ company, onClose }) => {
  const token = localStorage.getItem("token");
  const payload = JSON.parse(atob(token.split(".")[1])); // decode JWT
  const userRole = payload.role;
  const userId = payload._id || payload.id;
 console.log("Company Social Media",company.socialMedia)
  if (!company) return null;

  const openInNewTab = (url) =>
    url ? (
      <a
        href={url.startsWith("http") ? url : `https://${url}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline break-all"
      >
        {url}
      </a>
    ) : (
      "N/A"
    );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 px-4">
      <div className="bg-white w-full relative rounded-xl shadow-2xl max-w-5xl w-full p-8 pt-0 overflow-y-auto max-h-[90vh] border border-gray-200">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 flex justify-between items-center border-b pb-4 mt-2 mb-6">
          <h2 className="text-3xl font-semibold text-gray-800">
            {company.name}
          </h2>
          <div className="flex gap-8">
            {(userRole === "super_admin" || company.createdBy === userId) && (
              <>
                <ExportButtons data={[company]} />
              </>
            )}
            <button
              onClick={onClose}
              className="text-gray-900 hover:text-red-500 transition text-4xl font-light"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="space-y-8 text-[15px] text-gray-700 leading-relaxed">
          {/* Address & Contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-lg p-4 border">
              <div className="flex items-center gap-2 mb-2 text-lg font-semibold text-gray-900">
                <MapPin className="w-8 h-8 text-red-500 bg-red-100 rounded-full p-1 shadow" />
                Address
              </div>
              <p>
                <strong>Street: </strong>
                {company.address?.street || "N/A"}
              </p>
              <p>
                <strong>City: </strong>
                {company.address?.city || "N/A"}
              </p>
              <p>
                <strong>State: </strong>
                {company.address?.state || "N/A"}
              </p>
              <p>
                <strong>ZipCode: </strong>
                {company.address?.zipCode || "N/A"}
              </p>
              <p>
                <strong>Country: </strong>
                {company.address?.country || "N/A"}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border">
              <div className="flex items-center gap-2 mb-2 text-lg font-semibold text-gray-900">
                <Globe className="w-8 h-8 text-blue-600 bg-blue-100 rounded-full p-1" />
                Contact
              </div>
              <p className="flex items-center mt-3 gap-2">
                <Mail className="w-4 h-4" />
                {company.contact?.email || "N/A"}
              </p>
              <p className="flex items-center mt-3 gap-2">
                <Phone className="w-4 h-4" />
                {company.contact?.phone || "N/A"}
              </p>
              <p className="flex items-center mt-3 gap-2">
                <Globe className="w-4 h-4" />
                {openInNewTab(company.contact?.website)}
              </p>
            </div>
          </div>

          {/* Social Media */}
          <div>
            <div className="flex items-center gap-2 mb-2 text-lg font-semibold text-gray-900">
              Social Media
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-gray-50 p-4 rounded-lg border">
              <p className="flex items-center gap-3">
                <Globe className="w-8 h-8 text-red-600 bg-white rounded-full p-1 shadow" />
                {openInNewTab(company.socialMedia?.google)}
              </p>
              <p className="flex items-center gap-3">
                <Globe className="w-8 h-8text-[#8ec700] bg-white rounded-full p-1 shadow" />
                {openInNewTab(company.socialMedia?.kununu)}
              </p>
              <p className="flex items-center gap-3">
                <Globe className="w-8 h-8 text-indigo-600 bg-white rounded-full p-1 shadow" />
                {openInNewTab(company.socialMedia?.trustpilot)}
              </p>
              <p className="flex items-center gap-3">
                <Globe className="w-8 h-8 text-yellow-600 bg-white rounded-full p-1 shadow" />
                {openInNewTab(company.socialMedia?.golocal)}
              </p>
              <p className="flex items-center gap-3">
                <Globe className="w-8 h-8 text-purple-600 bg-white rounded-full p-1 shadow" />
                {openInNewTab(company.socialMedia?.jameda)}
              </p>
            </div>

          </div>

          {/* Business Hours */}
          <div>
            <h3 className="font-semibold text-gray-900 text-lg mb-2">
              Business Hours
            </h3>
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-left text-sm table-auto">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="p-3 border-b">Day</th>
                    <th className="p-3 border-b">Opens</th>
                    <th className="p-3 border-b">Closes</th>
                    <th className="p-3 border-b">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {company.businessHours?.map((day, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition border-b"
                    >
                      <td className="p-3">{day.day}</td>
                      <td className="p-3">{day.isClosed ? "—" : day.open}</td>
                      <td className="p-3">{day.isClosed ? "—" : day.close}</td>
                      <td className="p-3 font-medium">
                        {day.isClosed ? "Closed" : "Open"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Description */}
          <div>
            <h3 className="font-semibold text-gray-900 text-lg mb-2">
              Description
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg border">
              <p>{company.description || "N/A"}</p>
            </div>
          </div>

          {/* Categories */}
          <div>
            <h3 className="font-semibold text-gray-900 text-lg mb-2">
              Business Categories
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg border">
              <p>{company.categories?.join(", ") || "N/A"}</p>
            </div>
          </div>

          {/* Media Photos Slider */}
          {company.media?.photos?.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 text-lg mb-4">
                Company Photos
              </h3>
              <Swiper
                modules={[Navigation, Pagination]}
                navigation
                pagination={{ clickable: true }}
                spaceBetween={20}
                slidesPerView={1}
                className="rounded-lg"
              >
                {company.media.photos.map((photo, idx) => (
                  <SwiperSlide key={idx}>
                    <img
                      src={`${BASE_URL}${photo}`}
                      alt={`Company Photo ${idx + 1}`}
                      className="w-full h-[300px] object-contain rounded-lg border"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyViewModal;
